import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { API } from "../lib/api";
import { trackEvent } from "../lib/analytics";

// Module-level registry so only ONE clip plays at a time across the whole app.
const controllers = new Set();
function stopOthers(self) {
  controllers.forEach((c) => {
    if (c !== self) c.pause();
  });
}

export const DialoguePlayer = ({ date, slot, label, difficulty, mode = "dialogue", autoPlay = false, analyticsContext = "active" }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const ctrl = useRef({ pause: () => {} });

  const src = `${API}/challenge/${mode}/${date}/audio/${slot}`;

  useEffect(() => {
    const self = ctrl.current;
    self.pause = () => {
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
    };
    controllers.add(self);
    return () => controllers.delete(self);
  }, []);

  useEffect(() => {
    const a = new Audio(src);
    a.preload = "auto";
    audioRef.current = a;
    const onTime = () => a.duration && setProgress((a.currentTime / a.duration) * 100);
    const onPause = () => setPlaying(false);
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnd);
    if (autoPlay) {
      const tryPlay = () => {
        stopOthers(ctrl.current);
        a.play().then(() => setPlaying(true)).catch(() => {});
      };
      if (a.readyState >= 2) tryPlay();
      else a.addEventListener("canplay", tryPlay, { once: true });
    }
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnd);
    };
  }, [src, autoPlay]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      stopOthers(ctrl.current); // pause any other clip first
      trackEvent(analyticsContext === "completed" ? "post_completion_audio_played" : "audio_played", { slot, mode });
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  return (
    <div className="ac-card p-4 flex items-center gap-4" data-testid={`dialogue-clip-${slot}`}>
      <button
        onClick={toggle}
        data-testid={`dialogue-play-button-${slot}`}
        className="shrink-0 w-14 h-14 rounded-full btn-gold flex items-center justify-center gold-glow"
      >
        {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-bold text-amber-100">{label}</span>
          {difficulty && (
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">{difficulty}</span>
          )}
        </div>
        <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-[width] duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};
